import { OutlinedInput } from '@aibees/shared/components';
import { classnames } from '@aibees/shared/utils';
import { Box, MenuItem, Select, Stack, Typography } from '@mui/material';
import { memo, useState } from 'react';
import { useChangeTracker } from '../../context/change-tracker';
import { AccountAccordion } from '../account-accordion';
import { AccountManager } from '../account-manager';
import { AccountSeats } from '../account-seats';
import { InformationItem } from '../information-item';
import { DowngradeToFree } from './components/downgrade-to-free';
import { accountDetails, credits, getBillingSubscription } from './config';
import * as Styled from './styles';
import * as SX from './sx';
import type { OverviewTabProps as Props } from './types';

export const OverviewTab = memo(({ className }: Props) => {
  const { markDirty } = useChangeTracker();
  const [isDowngraded, setIsDowngraded] = useState(false);
  const billingSubscription = getBillingSubscription(isDowngraded);
  const [selectedPackage, setSelectedPackage] = useState('9K Credits / Month');

  const packageOptions = [
    '1K Credits / Month',
    '5K Credits / Month',
    '9K Credits / Month',
    '20K Credits / Month',
  ];

  return (
    <Styled.Wrapper
      className={classnames('OverviewTab', className)}
      sx={SX.root}
    >
      <Box>
        <AccountAccordion
          title="Record management"
          icon="user-setting"
          closeAble
          withLabelManagement
          summarySx={{ ...SX.accordion, borderTop: 0 }}
          detailSx={SX.detail}
          titleSx={SX.accordionTitle}
        >
          <AccountManager />
          <AccountSeats />
        </AccountAccordion>
      </Box>
      <Box>
        <AccountAccordion
          title="Account details"
          icon="user-details"
          closeAble
          summarySx={SX.accordion}
          detailSx={SX.detail}
          titleSx={SX.accordionTitle}
        >
          {accountDetails.map((i) => (
            <InformationItem key={i.title} item={i} isSwitch={i.isSwitch} />
          ))}
        </AccountAccordion>
        <AccountAccordion
          title="Billing overview"
          icon="billing"
          closeAble
          summarySx={SX.accordion}
          detailSx={SX.detail}
          titleSx={SX.accordionTitle}
        >
          <Stack px={1} py={0.5} gap={0.5}>
            <Typography variant="body4" fontWeight={400} color="grey.500">
              Package
            </Typography>
            <Select
              fullWidth
              value={selectedPackage}
              onChange={(e) => {
                setSelectedPackage(e.target.value);
                markDirty();
              }}
              size="medium"
              displayEmpty
            >
              {packageOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </Stack>
          <Stack px={1} py={0.5} gap={0.5}>
            <Typography variant="body4" fontWeight={400} color="grey.500">
              Subscription status
            </Typography>
            <Stack direction="row" alignItems="center" gap={0.5}>
              <Box flex={1}>
                <OutlinedInput
                  fullWidth
                  value={isDowngraded ? 'Downgrade to free' : 'Active'}
                  size="medium"
                  inputProps={{
                    readOnly: true,
                  }}
                />
              </Box>
              <DowngradeToFree
                isDowngraded={isDowngraded}
                setIsDowngraded={setIsDowngraded}
              />
            </Stack>
          </Stack>
          {billingSubscription.map((i) => (
            <InformationItem key={i.title} item={i} />
          ))}
          <Typography
            variant="body2"
            fontWeight={500}
            color="grey.700"
            px={1}
            py={0.5}
          >
            Credits
          </Typography>
          {credits.map((i) => (
            <InformationItem key={i.title} item={i} />
          ))}
        </AccountAccordion>
      </Box>
    </Styled.Wrapper>
  );
});

OverviewTab.displayName = 'OverviewTab';
